import type { SupportedLanguage } from './formatting-context';

/**
 * Canonical runtime list of supported languages, in the deterministic order
 * used by the resolver fallback chain (step 3 — first available translation).
 *
 * Source of truth for the {@link SupportedLanguage} type is
 * `formatting-context.ts`; keep this array in lock-step with that union.
 */
export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['en', 'de', 'fr', 'it'];

/**
 * The default fallback language used when the requested language has no value
 * for a given field.
 */
export const DEFAULT_TRANSLATION_LANGUAGE: SupportedLanguage = 'en';

/**
 * Per-language slot of translatable fields. Field names are constrained by
 * the entity-specific `Fields` parameter so consumers stay typed.
 */
export type TranslatableFieldsRecord<Fields extends string = string> = Partial<Record<Fields, string | null>>;

/**
 * Per-entity translations container.
 *
 * @example
 * ```ts
 * const value: TranslatableText<'name' | 'description'> = {
 *   en: { name: 'Zurich HQ', description: 'Main Swiss office' },
 *   de: { name: 'Zürich Hauptsitz' },
 * };
 * ```
 */
export type TranslatableText<Fields extends string = string> = Partial<Record<SupportedLanguage, TranslatableFieldsRecord<Fields>>>;

/**
 * Options for {@link resolveTranslation}.
 */
export interface ResolveTranslationOptions {
  /**
   * Fallback string returned after the JSONB-based fallback chain has been
   * exhausted (typically the value of a legacy non-translated column kept
   * during a dual-write transition).
   */
  legacy?: string | null;
  /**
   * Override the default fallback language. Defaults to `'en'`.
   */
  defaultLanguage?: SupportedLanguage;
}

/**
 * Resolve a single translatable field to a single string using the canonical
 * fallback chain:
 *
 * 1. `value[language][field]`
 * 2. `value[defaultLanguage][field]`
 * 3. `value[anyLanguage][field]` — first non-empty in deterministic order
 *    ({@link SUPPORTED_LANGUAGES})
 * 4. `legacy` (if provided and non-empty)
 * 5. `null`
 *
 * Empty strings and whitespace-only values are treated as absent.
 */
export function resolveTranslation<Fields extends string>(
  value: TranslatableText<Fields> | null | undefined,
  field: Fields,
  language: SupportedLanguage,
  options: ResolveTranslationOptions = {}
): string | null {
  const defaultLanguage = options.defaultLanguage ?? DEFAULT_TRANSLATION_LANGUAGE;

  // 1) requested language
  const requested = pickField(value, language, field);
  if (requested !== null) return requested;

  // 2) default language
  if (language !== defaultLanguage) {
    const fallback = pickField(value, defaultLanguage, field);
    if (fallback !== null) return fallback;
  }

  // 3) first available language (deterministic order)
  for (const candidate of SUPPORTED_LANGUAGES) {
    if (candidate === language || candidate === defaultLanguage) continue;
    const found = pickField(value, candidate, field);
    if (found !== null) return found;
  }

  // 4) legacy column value
  const legacy = options.legacy;
  if (typeof legacy === 'string' && legacy.trim().length > 0) return legacy;

  // 5) nothing to show
  return null;
}

/**
 * Resolve every requested field at once. Convenience wrapper around
 * {@link resolveTranslation}.
 */
export function resolveTranslatedFields<Fields extends string>(
  value: TranslatableText<Fields> | null | undefined,
  fields: readonly Fields[],
  language: SupportedLanguage,
  options: ResolveTranslationOptions = {}
): Record<Fields, string | null> {
  const resolved = {} as Record<Fields, string | null>;
  for (const field of fields) {
    resolved[field] = resolveTranslation(value, field, language, options);
  }
  return resolved;
}

/**
 * Return the first non-empty value for a given field across all languages, in
 * deterministic {@link SUPPORTED_LANGUAGES} order. Useful as a UI sub-helper
 * when the active language is unknown.
 */
export function getFirstAvailableTranslation<Fields extends string>(
  value: TranslatableText<Fields> | null | undefined,
  field: Fields
): string | null {
  if (value == null) return null;
  for (const language of SUPPORTED_LANGUAGES) {
    const found = pickField(value, language, field);
    if (found !== null) return found;
  }
  return null;
}

/**
 * Deep-merge two `TranslatableText` values. The shape is open: every language
 * present in `existing` survives unless `incoming` contains an entry that
 * explicitly overrides it. Field-level merging follows the same rule.
 *
 * - `null` and empty/whitespace-only strings in `incoming` clear the matching
 *   field (leaving the language slot intact, so callers can distinguish
 *   "field cleared" from "language untouched").
 * - Languages and fields omitted from `incoming` are left untouched.
 *
 * The function is pure: neither argument is mutated.
 */
export function mergeTranslations<Fields extends string>(
  existing: TranslatableText<Fields> | null | undefined,
  incoming: TranslatableText<Fields> | null | undefined
): TranslatableText<Fields> {
  if (incoming == null) {
    return cloneTranslations(existing ?? {});
  }
  const merged: TranslatableText<Fields> = cloneTranslations(existing ?? {});

  for (const language of Object.keys(incoming) as SupportedLanguage[]) {
    const incomingSlot = incoming[language];
    if (incomingSlot == null) continue;
    const existingSlot = merged[language] ?? {};
    const mergedSlot: TranslatableFieldsRecord<Fields> = { ...existingSlot };
    for (const field of Object.keys(incomingSlot) as Fields[]) {
      const next = incomingSlot[field];
      if (next === null) {
        mergedSlot[field] = null;
      } else if (typeof next === 'string') {
        const trimmed = next.trim();
        mergedSlot[field] = trimmed.length === 0 ? null : next;
      }
    }
    merged[language] = mergedSlot;
  }
  return merged;
}

/**
 * Build an initial translations object from a plain field map. Used by services
 * during create when the caller supplied legacy scalar fields but no
 * translations map.
 */
export function buildDefaultTranslations<Fields extends string>(
  fields: Partial<Record<Fields, string | null | undefined>>,
  language: SupportedLanguage = DEFAULT_TRANSLATION_LANGUAGE
): TranslatableText<Fields> {
  const slot: TranslatableFieldsRecord<Fields> = {};
  let hasAny = false;
  for (const [field, raw] of Object.entries(fields) as [Fields, string | null | undefined][]) {
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (trimmed.length === 0) continue;
    slot[field] = raw as string;
    hasAny = true;
  }
  if (!hasAny) return {};
  return { [language]: slot } as TranslatableText<Fields>;
}

/**
 * Type guard ensuring an unknown payload conforms to the `TranslatableText`
 * shape: every top-level key is a {@link SupportedLanguage}, every value is
 * either `null`, `undefined`, or a flat object whose values are strings,
 * `null`, or `undefined`.
 */
export function isTranslatableText(value: unknown): value is TranslatableText {
  if (value == null || typeof value !== 'object') return false;
  for (const [lang, slot] of Object.entries(value as Record<string, unknown>)) {
    if (!isSupportedLanguageCode(lang)) return false;
    if (!isValidLanguageSlot(slot)) return false;
  }
  return true;
}

function isValidLanguageSlot(slot: unknown): boolean {
  if (slot == null) return true;
  if (typeof slot !== 'object') return false;
  for (const fieldValue of Object.values(slot as Record<string, unknown>)) {
    if (fieldValue == null) continue;
    if (typeof fieldValue !== 'string') return false;
  }
  return true;
}

/**
 * Narrow a free-form string to a {@link SupportedLanguage}. Use sparingly —
 * prefer the static type wherever possible.
 */
export function isSupportedLanguageCode(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

function pickField<Fields extends string>(
  value: TranslatableText<Fields> | null | undefined,
  language: SupportedLanguage,
  field: Fields
): string | null {
  if (value == null) return null;
  const slot = value[language];
  if (slot == null) return null;
  const raw = slot[field];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : raw;
}

/**
 * Extract a per-field {@link TranslatableText} from a combined translations blob.
 *
 * The DB stores all translatable fields together:
 * `{ en: { name: 'Hello', description: 'World' }, de: { name: 'Hallo', description: 'Welt' } }`
 *
 * This helper extracts a single-field view suitable for binding to a
 * {@link TranslatedInput} component:
 * `extractFieldTranslations(blob, 'name')` → `{ en: { name: 'Hello' }, de: { name: 'Hallo' } }`
 */
export function extractFieldTranslations<F extends string>(
  translations: TranslatableText | null | undefined,
  field: F
): TranslatableText<F> {
  if (!translations) return {};
  const result: TranslatableText<F> = {};
  for (const [lang, slot] of Object.entries(translations) as [
    SupportedLanguage,
    Record<string, string | null | undefined> | null | undefined,
  ][]) {
    if (!slot || !(field in slot)) continue;
    result[lang] = { [field]: slot[field] } as Record<F, string | null>;
  }
  return result;
}

/**
 * Extract a per-field translation map with a scalar fallback.
 *
 * When the entity was fetched with `?include=translations` the blob contains
 * all languages; when fetched without it the blob is absent and the legacy
 * scalar (`entity.name`, `entity.label`, …) seeds an English-only fallback.
 *
 * @param translations - Raw `translations` blob from the entity response.
 * @param field        - The field key to extract (e.g. `'name'`, `'label'`).
 * @param legacyValue  - Fallback scalar used when the blob is absent.
 */
export function fieldTranslationsWithFallback<F extends string>(
  translations: TranslatableText | null | undefined,
  field: F,
  legacyValue: string | null | undefined
): TranslatableText<F> {
  const extracted = extractFieldTranslations(translations, field);
  if (Object.keys(extracted).length > 0) return extracted;
  if (!legacyValue?.trim()) return {} as TranslatableText<F>;
  return { [DEFAULT_TRANSLATION_LANGUAGE]: { [field]: legacyValue } } as TranslatableText<F>;
}

function cloneTranslations<Fields extends string>(value: TranslatableText<Fields>): TranslatableText<Fields> {
  const out: TranslatableText<Fields> = {};
  for (const [language, slot] of Object.entries(value) as [SupportedLanguage, TranslatableFieldsRecord<Fields> | undefined][]) {
    if (slot == null) continue;
    out[language] = { ...slot };
  }
  return out;
}
